/**
 * Print-Only Valuation Dashboard Page for PDF Generation
 * This page renders the full valuation dashboard content for PDF export
 *
 * URL: /print/valuation/:type/:businessId
 * Type: 'advanced' or 'simple'
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../../print.css';
import {
  calculateFinancialRatios,
  getRatioColorClass,
  getMethodDescription
} from '../../utils/valuationDashboardUtils';
import { RATIO_BENCHMARKS } from '../../utils/valuationDashboardConstants';

const PrintValuationDashboard = () => {
  const { type, businessId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Fetch all required data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('token');
        const token = urlToken || localStorage.getItem('token') || sessionStorage.getItem('token');

        // Get forecast parameters from URL (with defaults)
        const forecastGrowthRate = parseFloat(urlParams.get('growthRate')) || 15;
        const forecastEbitdaMargin = parseFloat(urlParams.get('ebitdaMargin')) || null;
        const forecastYears = parseInt(urlParams.get('years')) || 5;
        const forecastConfidenceRange = parseFloat(urlParams.get('confidenceRange')) || 25;

        // Use the same pattern as PrintClientProfile with X-Business-Id header
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Business-Id': businessId
        };

        // Fetch all data in parallel - use correct API routes with X-Business-Id header
        // Filter valuations by type (simple or advanced)
        const [businessRes, valuationsRes, profitGapRes, valueGapRes, wealthGapRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/business/profile`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/valuation/history?type=${type}`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/profit-gap`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/value-gap`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/wealth-gap`, { headers })
        ]);

        // Get industry data if we have industry_id in the valuation input data
        let industryResponse = null;
        const valuationsData = valuationsRes.ok ? await valuationsRes.json() : { valuations: [] };
        const latestVal = valuationsData.valuations?.[0] || null;

        if (latestVal?.input_data) {
          try {
            const parsedInput = typeof latestVal.input_data === 'string'
              ? JSON.parse(latestVal.input_data)
              : latestVal.input_data;
            const industryId = parsedInput.industry_id;

            if (industryId) {
              industryResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/valuation/industry/${industryId}`, { headers });
            }
          } catch (e) {
            console.error('Failed to parse input_data for industry_id:', e);
          }
        }

        if (!businessRes.ok) throw new Error('Failed to fetch business data');

        const businessData = await businessRes.json();
        const profitGapData = profitGapRes.ok ? await profitGapRes.json() : null;
        const valueGapData = valueGapRes.ok ? await valueGapRes.json() : null;
        const wealthGapData = wealthGapRes.ok ? await wealthGapRes.json() : null;
        const industryData = industryResponse?.ok ? await industryResponse.json() : null;


        const businessProfile = businessData.data?.profile || businessData;

        // Parse input_data from valuation if available to get financial data
        let parsedInputData = {};
        if (latestVal?.input_data) {
          try {
            parsedInputData = typeof latestVal.input_data === 'string'
              ? JSON.parse(latestVal.input_data)
              : latestVal.input_data;
          } catch (e) {
            console.error('Failed to parse input_data:', e);
          }
        }

        // Extract financial data for advanced valuations (multi-year data)
        let currentFinancials = {};
        if (type === 'advanced' && parsedInputData) {
          // Get the most recent year's data
          const getLatestYearValue = (field) => {
            const fieldData = parsedInputData[field];
            if (!fieldData || typeof fieldData !== 'object') return null;
            const years = Object.keys(fieldData).sort();
            const latestYear = years[years.length - 1];
            return fieldData[latestYear] || null;
          };

          currentFinancials = {
            revenue: getLatestYearValue('revenue'),
            gross_profit: getLatestYearValue('gross_profit'),
            ebitda: getLatestYearValue('ebitda'),
            net_income: getLatestYearValue('profit_after_tax') || getLatestYearValue('net_income'),
            total_assets: getLatestYearValue('total_assets'),
            total_liabilities: getLatestYearValue('total_liabilities'),
            cash: getLatestYearValue('cash'),
            inventory: getLatestYearValue('inventory'),
            current_assets: getLatestYearValue('current_assets'),
            current_liabilities: getLatestYearValue('current_liabilities')
          };

          // Calculate owners_equity (total_assets - total_liabilities)
          if (currentFinancials.total_assets && currentFinancials.total_liabilities) {
            currentFinancials.owners_equity = currentFinancials.total_assets - currentFinancials.total_liabilities;
          }
        } else if (type === 'simple' && parsedInputData) {
          // For simple valuations, data is not multi-year, just direct values
          currentFinancials = {
            revenue: parsedInputData.revenue,
            ebitda: parsedInputData.ebitda,
            net_income: parsedInputData.profit_after_tax || parsedInputData.net_income,
            // Simple valuations typically don't have these fields
            total_assets: parsedInputData.total_assets,
            total_liabilities: parsedInputData.total_liabilities,
            cash: parsedInputData.cash
          };

          // Calculate owners_equity if we have the data
          if (currentFinancials.total_assets && currentFinancials.total_liabilities) {
            currentFinancials.owners_equity = currentFinancials.total_assets - currentFinancials.total_liabilities;
          }
        }

        // Merge business profile with valuation input data (valuation data takes precedence for financials)
        const enrichedBusiness = {
          ...businessProfile,
          revenue: currentFinancials.revenue || parsedInputData.revenue || businessProfile.revenue,
          gross_profit: currentFinancials.gross_profit || parsedInputData.gross_profit || businessProfile.gross_profit,
          ebitda: currentFinancials.ebitda || parsedInputData.ebitda || businessProfile.ebitda,
          net_income: currentFinancials.net_income,
          total_assets: currentFinancials.total_assets,
          total_liabilities: currentFinancials.total_liabilities,
          cash: currentFinancials.cash,
          inventory: currentFinancials.inventory,
          current_assets: currentFinancials.current_assets,
          current_liabilities: currentFinancials.current_liabilities,
          owners_equity: currentFinancials.owners_equity
        };

        const finalValueGap = valueGapData?.value_gap || valueGapData;

        const finalForecastParams = {
          growthRate: forecastGrowthRate,
          ebitdaMargin: forecastEbitdaMargin,
          years: forecastYears,
          confidenceRange: forecastConfidenceRange
        };


        setData({
          business: enrichedBusiness,
          latestValuation: latestVal,
          profitGap: profitGapData?.profit_gap || profitGapData,
          valueGap: finalValueGap,
          wealthGap: wealthGapData?.wealth_gap || wealthGapData,
          industryData: industryData,
          forecastParams: finalForecastParams
        });

        setLoading(false);
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId]);

  // Set ready attribute for Playwright
  useEffect(() => {
    if (!loading && data) {
      document.body.setAttribute('data-pdf-ready', 'true');

      // Set document title
      document.title = `Valuation Dashboard Report`;
    }
  }, [loading, data]);

  if (loading) {
    return (
      <div className="print-container">
        <div className="loading">Loading valuation data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-container">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  const { business, latestValuation, profitGap, valueGap, wealthGap, industryData, forecastParams } = data;
  const isAdvanced = type === 'advanced';
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  // Calculate financial ratios for the business
  const financials = {
    revenue: business?.revenue || 0,
    ebitda: business?.ebitda || 0,
    grossProfit: business?.gross_profit || 0, // Estimate if not available
    assetValue: business?.total_assets || 0
  };
  const ratios = calculateFinancialRatios(financials, latestValuation);

  // Get short method name for display
  const methodFullName = (() => {
    const method = latestValuation?.method;
    if (!method) return 'N/A';

    // Short method name mapping (not full descriptions)
    const methodNames = {
      'cca': 'Comparable Company Analysis',
      'dcf': 'Discounted Cash Flow',
      'asset': 'Asset-Based Valuation',
      'sde': "Seller's Discretionary Earnings",
      'manual': 'Manual Multiple',
      'nav': 'Net Asset Value',
      'capitalization': 'Capitalization of Earnings',
      'multiple_of_ebitda': 'Multiple of EBITDA',
      'multiple': 'Multiple of EBITDA',
      'revenue_multiple': 'Revenue Multiple',
      'ebitda_multiple': 'EBITDA Multiple'
    };

    // Try to get short name from mapping first
    const shortName = methodNames[method.toLowerCase()];
    if (shortName) return shortName;

    // If not found, format the raw method name nicely (e.g., "multiple_of_ebitda" -> "Multiple Of Ebitda")
    return method
      .split('_')
      .map(word => word.toUpperCase() === 'EBITDA' ? 'EBITDA' : word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  })();

  // Helper function to determine status based on ratio value
  const getStatus = (ratioName, value) => {
    const color = getRatioColorClass(ratioName, value);
    return color === 'green' ? 'Good' : color === 'yellow' ? 'Acceptable' : 'Below Target';
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);

  const formatPercent = (value) => {
    if (value == null) return 'N/A';
    return `${value.toFixed(1)}%`;
  };

  const formatShortCurrency = (value) => {
    if (!value) return '$0';
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return formatCurrency(value);
  };

  // Calculate projected values using forecast parameters
  const growthRate = (forecastParams?.growthRate || 15) / 100; // Convert percentage to decimal
  const forecastYearsCount = forecastParams?.years || 5;
  const currentValue = latestValuation?.valuation_amount || 0;
  const projectedValue = currentValue * Math.pow(1 + growthRate, forecastYearsCount);

  const currentRevenue = business?.revenue || 0;
  const projectedRevenue = currentRevenue * Math.pow(1 + growthRate, forecastYearsCount);

  // Define cover page color based on type
  const coverColor = isAdvanced ? '#8b5cf6' : '#3b82f6';

  // Generate 5-year forecast data for charts
  const yearLabels = Array.from({length: 6}, (_, i) => {
    const year = new Date().getFullYear() + i;
    return i === 0 ? `${year} (Current)` : year.toString();
  });

  const revenueData = yearLabels.map((_, i) => {
    return currentRevenue * Math.pow(1 + growthRate, i);
  });

  const ebitdaData = yearLabels.map((_, i) => {
    const projRevenue = currentRevenue * Math.pow(1 + growthRate, i);
    const ebitda = business?.ebitda || 0;
    const margin = currentRevenue > 0 ? ebitda / currentRevenue : 0.15;
    return projRevenue * margin;
  });

  // Extract multi-year data for Income Statement and Balance Sheet trends (for advanced valuations)
  let incomeStatementYears = [];
  let incomeStatementData = { revenue: [], ebitda: [], sde: [] };
  let balanceSheetYears = [];
  let balanceSheetData = { assets: [], liabilities: [], equity: [] };

  if (isAdvanced && latestValuation?.input_data) {
    try {
      const inputData = typeof latestValuation.input_data === 'string'
        ? JSON.parse(latestValuation.input_data)
        : latestValuation.input_data;

      // Extract income statement multi-year data
      if (inputData.revenue && typeof inputData.revenue === 'object') {
        incomeStatementYears = Object.keys(inputData.revenue).sort();
        incomeStatementData.revenue = incomeStatementYears.map(y => inputData.revenue[y] || 0);
      }

      if (inputData.gross_profit && typeof inputData.gross_profit === 'object') {
        incomeStatementYears = Object.keys(inputData.gross_profit).sort();
        incomeStatementData.gross_profit = incomeStatementYears.map(y => inputData.gross_profit[y] || 0);
      }

      if (inputData.ebitda && typeof inputData.ebitda === 'object') {
        incomeStatementData.ebitda = incomeStatementYears.map(y => inputData.ebitda[y] || 0);
      }
      // Use actual profit_after_tax or net_income if available
      if (inputData.profit_after_tax && typeof inputData.profit_after_tax === 'object') {
        incomeStatementData.netIncome = incomeStatementYears.map(y => inputData.profit_after_tax[y] || 0);
      } else if (inputData.net_income && typeof inputData.net_income === 'object') {
        incomeStatementData.netIncome = incomeStatementYears.map(y => inputData.net_income[y] || 0);
      } else {
        // Fallback to empty array if no net income data
        incomeStatementData.netIncome = [];
      }

      // Use seller discretionary earnings if available
      if (inputData.sde && typeof inputData.sde === 'object') {
        incomeStatementData.sde = incomeStatementYears.map(y => inputData.sde[y] || 0);
      } else {
        // Fallback to empty array if no sde data
        incomeStatementData.sde = [];
      }
      // Extract balance sheet multi-year data
      if (inputData.total_assets && typeof inputData.total_assets === 'object') {
        balanceSheetYears = Object.keys(inputData.total_assets).sort();
        balanceSheetData.assets = balanceSheetYears.map(y => inputData.total_assets[y] || 0);
      }
      if (inputData.total_liabilities && typeof inputData.total_liabilities === 'object') {
        balanceSheetData.liabilities = balanceSheetYears.map(y => inputData.total_liabilities[y] || 0);
      }
      // Calculate equity as assets - liabilities
      balanceSheetData.equity = balanceSheetData.assets.map((a, i) => a - (balanceSheetData.liabilities[i] || 0));
    } catch (e) {
      console.error('Failed to extract multi-year data:', e);
    }
  }

  return (
    <div className="print-container" data-pdf-ready="true">
      {/* Cover Page */}
      <div className="print-page cover-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <div className="cover-header">
          <h1>Estimated Business Range of Value Report</h1>
          <div className="report-type">{isAdvanced ? 'Advanced Valuation Analysis' : 'Simple Valuation Analysis'}</div>
        </div>

        <div className="cover-business-info">
          <h2>{business?.business_name || business?.name || 'Business'}</h2>
          <p className="industry">{business?.industry || 'N/A'}</p>
        </div>

        <div className="cover-footer">
          <p>Generated: {today}</p>
          <p className="confidential">Confidential Business Range of Value Report</p>
        </div>
      </div>

      {/* Page 2: Business Overview & Valuation Summary */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Business Overview</h1>

        <table className="info-table">
          <tbody>
            <tr>
              <td style={{ width: '1.65in', fontSize: '9pt', fontWeight: 'bold', padding: '0.05in', color: '#111827', textAlign: 'right'}}>Business Name</td>
              <td style={{ fontSize: '9pt', padding: '0.05in', color: '#111827', textalign: 'middle' }}>{business?.business_name || business?.name || 'N/A'}</td>
              <td style={{ width: '0.15in', borderTop: 'none', borderBottom: 'none' }}></td>
              <td style={{ width: '2.10in', fontSize: '9pt', fontWeight: 'bold', padding: '0.05in', color: '#111827', textAlign: 'right' }}>Industry</td>
              <td style={{ fontSize: '9pt', padding: '0.05in', color: '#111827', textalign: 'middle' }}>{business?.industry || 'Not Specified'}</td>
            </tr>
            <tr>
              <td style={{ width: '1.65in', fontSize: '9pt', fontWeight: 'bold', padding: '0.05in', color: '#111827', textAlign: 'right' }}>Number of Employees</td>
              <td style={{ fontSize: '9pt', padding: '0.05in', color: '#111827', textalign: 'middle' }}>{business?.employees || 'Not Specified'}</td>
              <td style={{ width: '0.15in', borderTop: 'none', borderBottom: 'none' }}></td>
              <td style={{ width: '2.10in', fontSize: '9pt', fontWeight: 'bold', padding: '0.05in', color: '#111827', textAlign: 'right' }}>Year Founded</td>
              <td style={{ fontSize: '9pt', padding: '0.05in', color: '#111827', textalign: 'middle' }}>{business?.founded_year || business?.year_founded || 'Not Specified'}</td>
            </tr>
            <tr>
              <td style={{ width: '1.65in', fontSize: '9pt', fontWeight: 'bold', padding: '0.05in', color: '#111827', textAlign: 'right' }}>Location</td>
              <td style={{ fontSize: '9pt', padding: '0.05in', color: '#111827', textalign: 'middle' }}>{business?.primary_location || (business?.city && business?.state ? `${business.city}, ${business.state}` : business?.city || business?.state || 'Not Specified')}</td>
              <td style={{ width: '0.15in', borderTop: 'none', borderBottom: 'none' }}></td>
              <td style={{ width: '2.10in', fontSize: '9pt', fontWeight: 'bold', padding: '0.05in', color: '#111827', textAlign: 'right' }}>Primary Geographical Market</td>
              <td style={{ fontSize: '9pt', padding: '0.05in', color: '#111827', textalign: 'middle' }}>{business?.primary_market || business?.geographical_market || 'Not Specified'}</td>
            </tr>
            <tr>
              <td style={{ width: '1.65in', fontSize: '9pt', fontWeight: 'bold', padding: '0.05in', color: '#111827', textAlign: 'right' }}>Registration Type</td>
              <td style={{ fontSize: '9pt', padding: '0.05in', color: '#111827', textalign: 'middle' }}>{business?.registration_type || 'Not Specified'}</td>
              <td style={{ width: '0.15in', borderTop: 'none', borderBottom: 'none', borderRight: 'none' }}></td>
            </tr>
          </tbody>
        </table>

        {/* Top Metrics Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.08in', marginBottom: '0.35in', marginTop: '0.35in' }}>
          <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)', padding: '0.10in', borderRadius: '8px', color: 'white' }}>
            <div style={{ fontSize: '8pt', marginBottom: '0.05in', opacity: 0.9, fontWeight: 'bold', textAlign: 'center' }}>Median Exit Value</div>
            <div style={{ fontSize: '14pt', fontWeight: 'bold', textAlign: 'center' }}>{formatShortCurrency(latestValuation?.valuation_amount || 0)}</div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)', padding: '0.10in', borderRadius: '8px', color: 'white' }}>
            <div style={{ fontSize: '8pt', marginBottom: '0.05in', opacity: 0.9, fontWeight: 'bold', textAlign: 'center' }}>Range of Exit Value</div>
            <div style={{ fontSize: '10pt', fontWeight: 'bold', textAlign: 'center' }}>{latestValuation?.low_range && latestValuation?.high_range &&
                `${formatShortCurrency(latestValuation.low_range)} - ${formatShortCurrency(latestValuation.high_range)}`
              }
            </div>
          </div>
          <div style={{ background: '#f0fdf4', border: '1px solid #22c55e', padding: '0.10in', borderRadius: '8px' }}>
            <div style={{ fontSize: '8pt', color: '#166534', marginBottom: '0.05in', textAlign: 'center' }}>Annual Revenue</div>
            <div style={{ fontSize: '14pt', fontWeight: 'bold', color: '#16a34a', textAlign: 'center' }}>{formatShortCurrency(business?.revenue || 0)}</div>
          </div>

          <div style={{ background: '#ede9fe', border: '1px solid #8b5cf6', padding: '0.10in', borderRadius: '8px' }}>
            <div style={{ fontSize: '8pt', color: '#5b21b6', marginBottom: '0.05in', textAlign: 'center' }}>Annual EBITDA</div>
            <div style={{ fontSize: '14pt', fontWeight: 'bold', color: '#5b21b6', textAlign: 'center' }}>{formatShortCurrency(business?.ebitda || 0)}</div>
            <div style={{ fontSize: '8pt', color: '#5b21b6', marginTop: '0.03in', textAlign: 'center' }}>
              {business?.revenue && business?.ebitda ? formatPercent((business.ebitda / business.revenue) * 100) + ' Margin' : ''}
            </div>
          </div>

          <div style={{ background: '#dde9fe', border: '1px solid #4f46e5', padding: '0.10in', borderRadius: '8px' }}>
            <div style={{ fontSize: '8pt', color: '#4f46e5', marginBottom: '0.05in', textAlign: 'center' }}>Net Income</div>
            <div style={{ fontSize: '14pt', fontWeight: 'bold', color: '#4f46e5', textAlign: 'center' }}>
              {formatShortCurrency(business?.net_income || 0)}
            </div>
            <div style={{ fontSize: '8pt', color: '#5b21b6', marginTop: '0.03in', textAlign: 'center' }}>
              {business?.revenue && business?.net_income ? formatPercent((business.net_income / business.revenue) * 100) + ' Margin' : ''}
            </div>
          </div>
        </div>

        <h2 className="section-title" style={{ fontSize: '14pt', marginBottom: '0.10in' }}>Valuation Method</h2>

        <div style={{ background: '#f9fafb', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.10in', marginBottom: '0.25in' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.08in', marginBottom: '0.1in' }}>
            <div>
              <div style={{ fontSize: '10pt', color: '#6b7280', marginBottom: '0.03in' }}>Primary Method</div>
              <div style={{ fontSize: '9pt', fontWeight: 600, color: '#111827' }}>{methodFullName || latestValuation?.method || 'N/A'}</div>
            </div>
            <div>
              <div style={{ fontSize: '10pt', color: '#6b7280', marginBottom: '0.03in' }}>Private Discount</div>
              <div style={{ fontSize: '9pt', fontWeight: 600, color: '#111827' }}>{latestValuation?.private_discount ? `${latestValuation.private_discount}%` : '0%'}</div>
              <div style={{ fontSize: '8pt', color: '#9ca3af', marginTop: '0.02in' }}>Liquidity adjustment</div>
            </div>
            <div>
              <div style={{ fontSize: '10pt', color: '#6b7280', marginBottom: '0.03in' }}>Range of Exit Value</div>
              <div style={{ fontSize: '9pt', fontWeight: 600, color: '#111827' }}>
                {latestValuation?.low_range && latestValuation?.high_range
                  ? `${formatShortCurrency(latestValuation.low_range)} - ${formatShortCurrency(latestValuation.high_range)}`: 'N/A'}
              </div>
              <div style={{ fontSize: '8pt', color: '#9ca3af', marginTop: '0.02in' }}>Uncertainty range</div>
            </div>
            <div>
              <div style={{ fontSize: '10pt', color: '#6b7280', marginBottom: '0.03in' }}>Valuation Date</div>
              <div style={{ fontSize: '9pt', fontWeight: 600, color: '#111827' }}>
                {latestValuation?.created_at ? new Date(latestValuation.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '10pt', color: '#6b7280', marginBottom: '0.03in' }}>Exit Horizon</div>
              <div style={{ fontSize: '9pt', fontWeight: 600, color: '#111827' }}>2-5 years</div>
            </div>     
          </div>
        </div>

        {valueGap && latestValuation?.low_range && latestValuation?.high_range && (() => {
          // Use the actual valuation range from the selected valuation type
          const valuationLow = latestValuation.low_range;
          const valuationHigh = latestValuation.high_range;
          const valuationRange = valuationHigh - valuationLow;

          // Calculate valuation position using the assessment score
          const valuationPosition = valueGap.current_attractiveness
            ? (((valueGap.current_attractiveness - 17) / 83) * 100)
            : 0;

          // Calculate current exit value based on actual valuation range
          const currentExitValue = valuationLow + (valuationRange * (valuationPosition / 100));

          return (
            <>
              <h2 style={{ fontSize: '14pt', color: '#059669', marginTop: '0.3in', marginBottom: '0.15in' }}>
                Current Estimated Exit Value Calculation
              </h2>

              <div style={{ background: '#f0f9ff', border: '1px solid #3b82f6', borderRadius: '6px', padding: '0.15in', marginBottom: '0.15in' }}>
                <p style={{ fontSize: '10pt', fontWeight: 600, color: '#1e40af', marginBottom: '0.06in' }}>
                  Assessment Score Scaling (17% - 100%)
                </p>
                <p style={{ fontSize: '9pt', color: '#1e40af', margin: '0.03in 0' }}>
                  Assessment scores range from <strong>17% (minimum)</strong> to <strong>100% (maximum)</strong>.
                  A <strong>58% assessment</strong> represents an average business at approximately the <strong>midpoint (49%)</strong> of your valuation range.
                </p>
                <p style={{ fontSize: '8pt', color: '#1e40af', margin: '0.03in 0' }}>
                  Linear scaling: 17% → 0% (low), 58% → 49% (midpoint), 67% → 60% (green zone), 72% → 66% (best-in-class), 100% → 100% (high)
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
                <div style={{ background: '#f9fafb', borderLeft: '4px solid #6b7280', padding: '0.12in', borderRadius: '4px' }}>
                  <p style={{ fontSize: '9pt', fontWeight: 600, marginBottom: '0.06in' }}>Step 1: Scale Current Assessment Score</p>
                  <p style={{ fontSize: '8pt', margin: '0.02in 0' }}>
                    Valuation Position = (Assessment Score - 17) / (100 - 17) × 100
                  </p>
                  <p style={{ fontSize: '8pt', margin: '0.02in 0' }}>
                    = ({valueGap.current_attractiveness?.toFixed(1) || 0}% - 17) / 83 × 100
                  </p>
                  <p style={{ fontSize: '11pt', fontWeight: 'bold', marginTop: '0.04in' }}>
                    = {valuationPosition.toFixed(1)}%
                  </p>
                </div>

                <div style={{ background: '#f9fafb', borderLeft: '4px solid #6b7280', padding: '0.12in', borderRadius: '4px' }}>
                  <p style={{ fontSize: '9pt', fontWeight: 600, marginBottom: '0.06in' }}>Step 2: Calculate Current Exit Value</p>
                  <p style={{ fontSize: '8pt', margin: '0.02in 0' }}>
                    Current Exit Value = Low + (Range × Valuation Position)
                  </p>
                  <p style={{ fontSize: '8pt', margin: '0.02in 0' }}>
                    = {formatCurrency(valuationLow)} + ({formatCurrency(valuationRange)} × {valuationPosition.toFixed(1)}%)
                  </p>
                  <p style={{ fontSize: '11pt', fontWeight: 'bold', marginTop: '0.04in' }}>
                    = {formatCurrency(currentExitValue)}
                  </p>
                </div>
              </div>
            </>
          );
        })()}
      </div>

      {/* Page 3: Dashboard Metrics & Valuation Details */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        {/* Financial Ratios - Card Style */}
        {isAdvanced && (
          <>
            <h2 className="section-title" style={{ fontSize: '14pt', marginBottom: '0.15in', marginTop: '0.25in' }}>Financial Ratios</h2>

            {/* Profitability Ratios */}
            <div style={{ marginBottom: '0.10in' }}>
              <div style={{ fontSize: '11pt', fontWeight: 600, color: '#dc2626', marginBottom: '0.08in', display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '0.05in' }}>💰</span> PROFITABILITY:
                <span style={{ fontSize: '9pt', color: '#111827', fontWeight: 400, marginLeft: '0.05in' }}>Measures a company's ability to generate profit relative to revenue, assets, or equity.</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.1in' }}>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '0.12in', marginBottom: '0.10in' }}>
                  <div style={{ fontSize: '10pt', color: '#166534', marginBottom: '0.08in' }}>Gross Margin: 
                    <span style={{ fontSize: '14pt', fontWeight: 'bold', color: '#16a34a', marginLeft: '0.10in' }}>{ratios.grossMargin > 0 ? formatPercent(ratios.grossMargin) : 'N/A'}</span>
                  </div>
                  <div style={{ fontSize: '8pt', color: '#000000', fontweight: 'bold', marginTop: '0.05in' }}>Calculation: (Gross Profit ÷ Revenue) × 100
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>Gross Margin measures profitability after cost of goods sold.
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>Higher gross margins indicate stronger pricing power and operational efficiency. Technology and service businesses typically have higher margins (50-80%) while retail and manufacturing may be lower (20-40%).
                  </div>
                </div>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '0.12in', marginBottom: '0.10in' }}>
                  <div style={{ fontSize: '10pt', color: '#166534', marginBottom: '0.08in' }}>EBITDA Margin: 
                    <span style={{ fontSize: '14pt', fontWeight: 'bold', color: '#16a34a', marginLeft: '0.10in' }}>{ratios.ebitdaMargin > 0 ? formatPercent(ratios.ebitdaMargin) : 'N/A'}</span>
                  </div>
                  <div style={{ fontSize: '8pt', color: '#000000', fontweight: 'bold', marginTop: '0.05in' }}>Calculation: (EBITDA ÷ Revenue) × 100
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>EBITDA margin shows operational profitability before interest, taxes, depreciation, and amortization
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>Strong EBITDA margins ({'>'}20%) indicate efficient operations. Industry standards vary: SaaS (20-30%), Manufacturing (10-15%), Retail (5-10%).
                  </div>
                </div>
              </div>
            </div>

            {/* Liquidity Ratios */}
            <div style={{ marginBottom: '0.10in' }}>
              <div style={{ fontSize: '11pt', fontWeight: 600, color: '#2563eb', marginBottom: '0.08in', display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '0.05in' }}>💧</span> LIQUIDITY: 
                <span style={{ fontSize: '9pt', color: '#111827', fontWeight: 400, marginLeft: '0.05in' }}> Measures a company's ability to pay short-term obligations and meet immediate financial needs.</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.1in' }}>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '0.12in', marginBottom: '0.10in' }}>
                  <div style={{ fontSize: '10pt', color: '#1e40af', marginBottom: '0.08in' }}>Current Ratio:
                    <span style={{ fontSize: '14pt', fontWeight: 'bold', color: '#2563eb', marginLeft: '0.10in' }}>{ratios.currentRatio > 0 ? ratios.currentRatio.toFixed(2) + 'x' : 'N/A'}</span>
                  </div>
                  <div style={{ fontSize: '8pt', color: '#000000', fontweight: 'bold', marginTop: '0.05in' }}>Calculation: Current Assets ÷ Current Liabilities
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>Current ratio measures ability to pay short-term obligations.
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>A ratio above 2.0 indicates strong short-term financial health. Between 1.0-2.0 is acceptable. Below 1.0 may indicate liquidity concerns.
                  </div>
                </div>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '0.12in', marginBottom: '0.10in' }}>
                  <div style={{ fontSize: '11pt', color: '#1e40af', marginBottom: '0.08in' }}>Cash Ratio:
                    <span style={{ fontSize: '14pt', fontWeight: 'bold', color: '#2563eb', marginLeft: '0.10in' }}>{ratios.cashRatio > 0 ? ratios.cashRatio.toFixed(2) + 'x' : 'N/A'}</span>
                  </div>
                  <div style={{ fontSize: '8pt', color: '#000000', fontweight: 'bold', marginTop: '0.05in' }}>Calculation: Cash ÷ Current Liabilities
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>Cash ratio is the most conservative liquidity measure.
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>Shows ability to pay current liabilities using only cash. {'>'}0.5 is excellent, 0.2-0.5 is acceptable, {'<'}0.2 may indicate cash flow concerns.
                  </div>
                </div>
              </div>
            </div>

            {/* Leverage Ratios */}
            <div style={{ marginBottom: '0.10in' }}>
              <div style={{ fontSize: '11pt', fontWeight: 600, color: '#f97316', marginBottom: '0.08in', display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '0.05in' }}>⚖️</span> LEVERAGE: 
                <span style={{ fontSize: '9pt', color: '#111827', fontWeight: 400, marginLeft: '0.05in' }}> Measures the degree to which a company uses debt financing.</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.1in' }}>
                <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '6px', padding: '0.12in', marginBottom: '0.10in' }}>
                  <div style={{ fontSize: '11pt', color: '#92400e', marginBottom: '0.08in' }}>Debt to Equity:
                    <span style={{ fontSize: '14pt', fontWeight: 'bold', color: '#ea580c', marginLeft: '0.10in' }}>{ratios.debtToEquity > 0 ? ratios.debtToEquity.toFixed(2) + 'x' : 'N/A'}</span>
                  </div>
                  <div style={{ fontSize: '8pt', color: '#000000', fontweight: 'bold', marginTop: '0.05in' }}>Calculation: Total Liabilities ÷ Equity
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>Debt-to-equity ratio shows financial leverage and risk.
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>Lower is generally better. {'<'}0.5 is conservative, 0.5-1.5 is moderate, {'>'}2.0 indicates high leverage and financial risk. Capital-intensive industries may have higher acceptable ratios.
                  </div>
                </div>
                <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '6px', padding: '0.12in', marginBottom: '0.10in' }}>
                  <div style={{ fontSize: '10pt', color: '#92400e', marginBottom: '0.08in' }}>Debt to Assets:
                    <span style={{ fontSize: '14pt', fontWeight: 'bold', color: '#ea580c', marginLeft: '0.10in' }}>{ratios.debtToAssets > 0 ? formatPercent(ratios.debtToAssets) : 'N/A'}</span>
                  </div>
                  <div style={{ fontSize: '8pt', color: '#000000', fontweight: 'bold', marginTop: '0.05in' }}>Calculation: (Total Liabilities ÷ Total Assets) × 100
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>Debt-to-assets ratio shows percentage of assets financed by debt.
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>Lower percentages indicate less financial risk. {'<'}30% is strong, 30%-50% is acceptable, {'>'}60% may indicate over-leverage.
                  </div>
                </div>
              </div>
            </div>

            {/* Efficiency Ratios */}
            <div style={{ marginBottom: '0.10in' }}>
              <div style={{ fontSize: '11pt', fontWeight: 600, color: '#16a34a', marginBottom: '0.08in', display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '0.05in' }}>⚡</span> EFFICIENCY: 
                <span style={{ fontSize: '9pt', color: '#111827', fontWeight: 400, marginLeft: '0.05in' }}> Measures how effectively a company uses its assets and resources to generate revenue and profit.</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.1in' }}>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '0.10in', marginBottom: '0.10in' }}>
                  <div style={{ fontSize: '10pt', color: '#166534', marginBottom: '0.08in' }}>Asset Turnover:
                    <span style={{ fontSize: '14pt', fontWeight: 'bold', color: '#16a34a', marginLeft: '0.10in' }}>{ratios.assetTurnover > 0 ? ratios.assetTurnover.toFixed(2) + 'x' : 'N/A'}</span>
                  </div>
                  <div style={{ fontSize: '8pt', color: '#000000', fontweight: 'bold', marginTop: '0.05in' }}>Calculation: Revenue ÷ Total Assets
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>Asset turnover measures how efficiently assets generate revenue.
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>Higher ratios indicate better asset utilization. Retail (2-3x), Services (1-2x), Manufacturing (0.5-1.5x). Varies significantly by industry.
                  </div>
                </div>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '0.10in', marginBottom: '0.10in' }}>
                  <div style={{ fontSize: '10pt', color: '#166534', marginBottom: '0.08in' }}>Return on Assets:
                    <span style={{ fontSize: '14pt', fontWeight: 'bold', color: '#16a34a', marginLeft: '0.10in' }}>{ratios.roa > 0 ? formatPercent(ratios.roa) : 'N/A'}</span>
                  </div>
                  <div style={{ fontSize: '8pt', color: '#000000', fontweight: 'bold', marginTop: '0.05in' }}>Calculation: (EBITDA ÷ Total Assets) × 100
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>Return on Assets (ROA) measures profitability relative to total assets.
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>Higher ROA indicates more efficient use of assets. {'>'}15% is excellent, 5-15% is good, {'<'}5% may indicate inefficient operations.
                  </div>
                </div>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '0.10in', marginBottom: '0.10in' }}>
                  <div style={{ fontSize: '10pt', color: '#166534', marginBottom: '0.08in' }}>Return on Equity:
                    <span style={{ fontSize: '14pt', fontWeight: 'bold', color: '#16a34a', marginLeft: '0.10in' }}>{ratios.roe > 0 ? formatPercent(ratios.roe) : 'N/A'}</span>
                  </div>
                  <div style={{ fontSize: '8pt', color: '#000000', fontweight: 'bold', marginTop: '0.05in' }}>Calculation: (EBITDA ÷ Equity) × 100
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>Return on Equity (ROE) measures profitability for shareholders.
                  </div>
                  <div style={{ fontSize: '8pt', color: '#6b7280', fontweight: 'bold', marginTop: '0.05in' }}>Higher ROE indicates better returns for shareholders. {'>'}20% is excellent, 10-20% is good, {'<'}10% may be concerning. Compare to industry peers.
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Growth Forecast Analysis - Show on Page 3 for Simple Valuations */}
        {!isAdvanced && (
          <>
            <h1 className="section-title" style={{ marginTop: '0.15in' }}>Growth Forecast Analysis</h1>

            <div style={{ background: '#f9fafb', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0.05in', marginBottom: '0.05in', marginTop: '0.08in' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr', gap: '0.08in' }}>
                <div><h3 style={{ fontSize: '9pt', fontWeight: 600, marginBottom: '0.03in', color: '#374151' }}>Forecast Assumptions:</h3></div>
                <div>
                  <p style={{ fontSize: '8pt', color: '#6b7280', margin: 0 }}>Annual Growth Rate:
                  <span style={{ fontSize: '10pt', fontWeight: 'bold', margin: '0.02in 0', color: '#10b981' }}> {forecastParams?.growthRate ? formatPercent(forecastParams.growthRate) : 'N/A'}</span>
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '8pt', color: '#6b7280', margin: 0 }}>EBITDA Margin:
                  <span style={{ fontSize: '10pt', fontWeight: 'bold', margin: '0.02in 0', color: '#9333ea' }}> {forecastParams?.ebitdaMargin ? formatPercent(forecastParams.ebitdaMargin) :
                     (business?.revenue && business?.ebitda ? formatPercent((business.ebitda / business.revenue) * 100) : 'N/A')}
                  </span>
                  </p>
                </div>
              </div>
            </div>

            <div style={{ background: '#f0f9ff', border: '1px solid #3b82f6', borderRadius: '6px', padding: '0.05in', marginBottom: '0.05in' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.05in' }}>
                <div><p style={{ fontSize: '8pt', margin: '0.03in 0' }}>Current Median Value: <strong>{formatShortCurrency(currentValue)}</strong>
                </p></div>
                <div><p style={{ fontSize: '8pt', margin: '0.03in 0' }}>{forecastYearsCount}-Year Projected Value: <strong>{formatShortCurrency(projectedValue)}</strong>
                </p></div>
                <div><p style={{ fontSize: '8pt', margin: '0.03in 0', color: '#6b7280' }}>
                  Based on {forecastParams?.growthRate || 15}% annual growth rate with ±{forecastParams?.confidenceRange || 25}% confidence range
                </p>
                </div>
              </div>
            </div>

            {/* Business Exit Value Growth Forecast Chart */}
            <div style={{ marginTop: '0.05in' }}>
              <svg width="100%" height="339" viewBox="0 0 968 339" xmlns="http://www.w3.org/2000/svg">
                <text x="484" y="5" textAnchor="middle" fontSize="18" fontWeight="600" fill="#1e3a8a">
                  Business Exit Value Growth Forecast
                </text>

                {(() => {
                  // Generate forecast with confidence range using actual parameters
                  const chartGrowthRate = (forecastParams?.growthRate || 15) / 100; // Convert percentage to decimal
                  const chartConfidenceRange = (forecastParams?.confidenceRange || 25) / 100; // Convert percentage to decimal
                  const chartYearsCount = forecastParams?.years || 5;


                  const years = Array.from({ length: chartYearsCount + 1 }, (_, i) => new Date().getFullYear() + i);

                  // Calculate projected values
                  const projectedValues = years.map((_, i) => currentValue * Math.pow(1 + chartGrowthRate, i));
                  const upperRange = projectedValues.map(val => val * (1 + chartConfidenceRange));
                  const lowerRange = projectedValues.map(val => val * (1 - chartConfidenceRange));

                  const maxValue = Math.max(...upperRange);
                  const minValue = Math.min(...lowerRange);
                  const valueRange = maxValue - minValue;

                  const getY = (value) => 290 - ((value - minValue) / valueRange * 218);
                  const getX = (index) => 121 + (index * (726 / (years.length - 1)));

                  // Create points for confidence range polygon
                  const upperPoints = upperRange.map((val, i) => `${getX(i)},${getY(val)}`).join(' ');
                  const lowerPoints = lowerRange.map((val, i) => `${getX(i)},${getY(val)}`).reverse().join(' ');
                  const rangePolygon = upperPoints + ' ' + lowerPoints;

                  // Main line points
                  const mainPoints = projectedValues.map((val, i) => `${getX(i)},${getY(val)}`).join(' ');

                  return (
                    <>
                      {/* Y-axis grid lines and labels */}
                      {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
                        const value = minValue + (valueRange * (1 - pct));
                        const y = 73 + (218 * pct);
                        return (
                          <g key={i}>
                            <line x1="121" y1={y} x2="847" y2={y} stroke="#e5e7eb" strokeWidth="1" />
                            <text x="109" y={y + 4} textAnchor="end" fontSize="11" fill="#6b7280">
                              {formatShortCurrency(value)}
                            </text>
                          </g>
                        );
                      })}

                      {/* X-axis labels */}
                      {years.map((year, i) => {
                        const x = getX(i);
                        return (
                          <text key={i} x={x} y="315" textAnchor="middle" fontSize="11" fill="#6b7280">
                            {year}
                          </text>
                        );
                      })}

                      {/* Confidence range (shaded area) */}
                      <polygon
                        points={rangePolygon}
                        fill="#3b82f6"
                        opacity="0.15"
                      />

                      {/* Upper confidence line */}
                      <polyline
                        points={upperPoints}
                        fill="none"
                        stroke="#93c5fd"
                        strokeWidth="1.5"
                        strokeDasharray="5,5"
                      />

                      {/* Lower confidence line */}
                      <polyline
                        points={lowerRange.map((val, i) => `${getX(i)},${getY(val)}`).join(' ')}
                        fill="none"
                        stroke="#93c5fd"
                        strokeWidth="1.5"
                        strokeDasharray="5,5"
                      />

                      {/* Main projection line */}
                      <polyline
                        points={mainPoints}
                        fill="none"
                        stroke="#2563eb"
                        strokeWidth="3"
                      />

                      {/* Data points */}
                      {projectedValues.map((val, i) => (
                        <g key={i}>
                          <circle cx={getX(i)} cy={getY(val)} r="5" fill="#2563eb" />
                          <circle cx={getX(i)} cy={getY(val)} r="3" fill="white" />
                        </g>
                      ))}

                      {/* Value labels on points */}
                      {projectedValues.map((val, i) => (
                        <text
                          key={i}
                          x={getX(i) + 10}
                          y={getY(val) - 12}
                          textAnchor="middle"
                          fontSize="11"
                          fontWeight="600"
                          fill="#1e40af"
                        >
                          {formatShortCurrency(val)}
                        </text>
                      ))}

                      {/* Legend */}
                      <line x1="350" y1="40" x2="380" y2="40" stroke="#2563eb" strokeWidth="3" />
                      <text x="385" y="44" fontSize="12" fill="#6b7280">Projected Median Value</text>

                      <line x1="530" y1="40" x2="560" y2="40" stroke="#93c5fd" strokeWidth="1.5" strokeDasharray="5,5" />
                      <text x="565" y="44" fontSize="12" fill="#6b7280">Range</text>
                    </>
                  );
                })()}
              </svg>
            </div>

            {/* Revenue & EBITDA Growth Chart */}
            <div style={{ marginTop: '0.03in', pageBreakInside: 'avoid' }}>
              <svg width="100%" height="339" viewBox="0 0 968 339" xmlns="http://www.w3.org/2000/svg">
                <text x="484" y="5" textAnchor="middle" fontSize="18" fontWeight="600" fill="#1e3a8a">
                  Revenue & EBITDA Growth Forecast
                </text>

                {(() => {
                  // Generate EBITDA forecast data
                  const currentRevenue = business?.revenue || 0;
                  if (currentRevenue === 0) {
                    return (
                      <text x="400" y="175" textAnchor="middle" fontSize="14" fill="#6b7280">
                        No revenue data available
                      </text>
                    );
                  }

                  const chartGrowthRate = (forecastParams?.growthRate || 15) / 100;
                  const chartYearsCount = forecastParams?.years || 5;


                  // Use ebitdaMargin from forecastParams or calculate from current data
                  const currentEbitdaMarginPercent = forecastParams?.ebitdaMargin ||
                    (business?.ebitda && business?.revenue ? (business.ebitda / business.revenue) * 100 : 20);
                  const currentEbitdaMargin = currentEbitdaMarginPercent / 100;


                  // Get industry high margin from profit gap
                  const industryHighMarginPercent = profitGap?.industry_high_ebitda_margin || 0;
                  const industryHighMargin = industryHighMarginPercent / 100;

                  // Generate yearly data points
                  const years = Array.from({ length: chartYearsCount + 1 }, (_, i) => new Date().getFullYear() + i);
                  const forecastData = years.map((_, i) => {
                    const projectedRevenue = currentRevenue * Math.pow(1 + chartGrowthRate, i);
                    const projectedCurrentEbitda = projectedRevenue * currentEbitdaMargin;
                    const projectedTargetEbitda = industryHighMargin > 0 ? projectedRevenue * industryHighMargin : 0;
                    const profitGapAmount = Math.max(0, projectedTargetEbitda - projectedCurrentEbitda);

                    return {
                      year: years[i],
                      revenue: projectedRevenue,
                      currentEbitda: projectedCurrentEbitda,
                      targetEbitda: projectedTargetEbitda,
                      profitGapAmount: profitGapAmount
                    };
                  });

                  // Calculate scales
                  const maxRevenue = Math.max(...forecastData.map(d => d.revenue));
                  const maxEbitda = Math.max(...forecastData.map(d => d.targetEbitda || d.currentEbitda));
                  const maxValue = Math.max(maxRevenue, maxEbitda);
                  const minValue = 0;
                  const valueRange = maxValue - minValue;

                  const getY = (value) => 290 - ((value - minValue) / valueRange * 218);
                  const getX = (index) => 121 + (index * (726 / (years.length - 1)));

                  // Create polygon points for stacked areas
                  // Revenue area (green fill)
                  const revenuePoints = forecastData.map((d, i) => `${getX(i)},${getY(d.revenue)}`);
                  const revenueBottomPoints = forecastData.map((_, i) => `${getX(i)},${getY(0)}`).reverse();
                  const revenuePolygon = revenuePoints.join(' ') + ' ' + revenueBottomPoints.join(' ');

                  // Current EBITDA stacked area (purple)
                  const currentEbitdaPoints = forecastData.map((d, i) => `${getX(i)},${getY(d.currentEbitda)}`);
                  const currentEbitdaBottomPoints = forecastData.map((_, i) => `${getX(i)},${getY(0)}`).reverse();
                  const currentEbitdaPolygon = currentEbitdaPoints.join(' ') + ' ' + currentEbitdaBottomPoints.join(' ');

                  // Profit Gap stacked on top of current EBITDA (orange)
                  const profitGapTopPoints = forecastData.map((d, i) =>
                    `${getX(i)},${getY(d.currentEbitda + d.profitGapAmount)}`
                  );
                  const profitGapBottomPoints = forecastData.map((d, i) =>
                    `${getX(i)},${getY(d.currentEbitda)}`
                  ).reverse();
                  const profitGapPolygon = profitGapTopPoints.join(' ') + ' ' + profitGapBottomPoints.join(' ');

                  // Target EBITDA line points
                  const targetEbitdaPoints = forecastData.map((d, i) => `${getX(i)},${getY(d.targetEbitda)}`).join(' ');

                  return (
                    <>
                      {/* Y-axis grid lines and labels */}
                      {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
                        const value = minValue + (valueRange * (1 - pct));
                        const y = 73 + (218 * pct);
                        return (
                          <g key={i}>
                            <line x1="121" y1={y} x2="847" y2={y} stroke="#e5e7eb" strokeWidth="1" />
                            <text x="109" y={y + 4} textAnchor="end" fontSize="11" fill="#6b7280">
                              {formatShortCurrency(value)}
                            </text>
                          </g>
                        );
                      })}

                      {/* X-axis labels */}
                      {years.map((year, i) => {
                        const x = getX(i);
                        return (
                          <text key={i} x={x} y="315" textAnchor="middle" fontSize="11" fill="#6b7280">
                            {year}
                          </text>
                        );
                      })}

                      {/* Revenue area (green with light opacity) */}
                      <polygon
                        points={revenuePolygon}
                        fill="#22c55e"
                        opacity="0.15"
                      />

                      {/* Revenue line */}
                      <polyline
                        points={revenuePoints.join(' ')}
                        fill="none"
                        stroke="#22c55e"
                        strokeWidth="2"
                      />

                      {/* Current EBITDA stacked area (purple) */}
                      <polygon
                        points={currentEbitdaPolygon}
                        fill="#9333ea"
                        opacity="0.3"
                      />

                      {/* Current EBITDA line */}
                      <polyline
                        points={currentEbitdaPoints.join(' ')}
                        fill="none"
                        stroke="#7c3aed"
                        strokeWidth="2"
                      />

                      {/* Profit Gap stacked area (orange) */}
                      {industryHighMargin > 0 && (
                        <polygon
                          points={profitGapPolygon}
                          fill="#f59e0b"
                          opacity="0.3"
                        />
                      )}

                      {/* Target EBITDA dashed line (purple) */}
                      {industryHighMargin > 0 && (
                        <polyline
                          points={targetEbitdaPoints}
                          fill="none"
                          stroke="#ee4242"
                          strokeWidth="2"
                          strokeDasharray="5,5"
                        />
                      )}

                      {/* Data points and value labels for revenue */}
                      {forecastData.map((d, i) => (
                        <g key={`rev-${i}`}>
                          <circle cx={getX(i)} cy={getY(d.revenue)} r="5" fill="#22c55e" />
                          <circle cx={getX(i)} cy={getY(d.revenue)} r="3" fill="white" />
                          <text
                            x={getX(i)}
                            y={getY(d.revenue) - 12}
                            textAnchor="middle"
                            fontSize="11"
                            fontWeight="600"
                            fill="#15803d"
                          >
                            {formatShortCurrency(d.revenue)}
                          </text>
                        </g>
                      ))}

                      {/* Data points and value labels for current EBITDA */}
                      {forecastData.map((d, i) => (
                        <g key={`curr-ebitda-${i}`}>
                          <circle cx={getX(i)} cy={getY(d.currentEbitda)} r="5" fill="#7c3aed" />
                          <circle cx={getX(i)} cy={getY(d.currentEbitda)} r="3" fill="white" />
                          <text
                            x={getX(i) + 25}
                            y={getY(d.currentEbitda) + 20}
                            textAnchor="middle"
                            fontSize="11"
                            fontWeight="600"
                            fill="#6b21a8"
                            outline="1px solid #ffffff"
                          >
                            {formatShortCurrency(d.currentEbitda)}
                          </text>
                        </g>
                      ))}

                      {/* Data points and value labels for target EBITDA */}
                      {industryHighMargin > 0 && forecastData.map((d, i) => (
                        <g key={`target-ebitda-${i}`}>
                          <circle cx={getX(i)} cy={getY(d.targetEbitda)} r="5" fill="#ee4242" />
                          <circle cx={getX(i)} cy={getY(d.targetEbitda)} r="3" fill="white" />
                          <text
                            x={getX(i)}
                            y={getY(d.targetEbitda) - 12}
                            textAnchor="middle"
                            fontSize="11"
                            fontWeight="600"
                            fill="#ee4242"
                          >
                            {formatShortCurrency(d.targetEbitda)}
                          </text>
                        </g>
                      ))}

                      {/* Legend */}
                      <g transform="translate(250, 40)">
                        {/* Revenue */}
                        <line x1="0" y1="0" x2="25" y2="0" stroke="#22c55e" strokeWidth="2" />
                        <text x="30" y="4" fontSize="11" fill="#6b7280">Revenue</text>

                        {/* Target EBITDA */}
                        {industryHighMargin > 0 && (
                          <>
                            <line x1="390" y1="0" x2="415" y2="0" stroke="#ee4242" strokeWidth="2" strokeDasharray="5,5" />
                            <text x="420" y="4" fontSize="11" fill="#6b7280">Target EBITDA</text>
                          </>
                        )}

                        {/* Current EBITDA */}
                        <rect x="120" y="-6" width="20" height="12" fill="#9333ea" opacity="0.5" />
                        <text x="145" y="4" fontSize="11" fill="#6b7280">Current EBITDA</text>

                        {/* Profit Gap */}
                        {industryHighMargin > 0 && (
                          <>
                            <rect x="270" y="-6" width="20" height="12" fill="#f59e0b" opacity="0.5" />
                            <text x="295" y="4" fontSize="11" fill="#6b7280">Profit Gap</text>
                          </>
                        )}
                      </g>
                    </>
                  );
                })()}
              </svg>
            </div>
          </>
        )}
      </div>

      {/* Page 4: Growth Metrics & Financial Trends */}
      {isAdvanced && (
        <div className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>
          <h1 className="section-title">Growth Metrics</h1>

          {/* CAGR Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.15in', marginBottom: '0.3in' }}>
            <div style={{ background: '#eff6ff', border: '1px solid #3b82f6', borderRadius: '8px', padding: '0.15in' }}>
              <div style={{ fontSize: '8pt', color: '#1e40af', marginBottom: '0.04in' }}>REVENUE CAGR</div>
              <div style={{ fontSize: '17pt', fontWeight: 'bold', color: '#2563eb' }}>
                {incomeStatementData.revenue.length > 1 ?
                  formatPercent(((Math.pow(incomeStatementData.revenue[incomeStatementData.revenue.length - 1] / incomeStatementData.revenue[0], 1 / (incomeStatementData.revenue.length - 1)) - 1) * 100))
                  : '0%'}
              </div>
              <div style={{ fontSize: '8pt', color: '#6b7280', marginTop: '0.02in' }}>Annual Growth Rate</div>
            </div>

            <div style={{ background: '#f7eeff', border: '1px solid #9333ea', borderRadius: '8px', padding: '0.15in' }}>
              <div style={{ fontSize: '8pt', color: '#9333ea', marginBottom: '0.04in' }}>EBITDA CAGR</div>
              <div style={{ fontSize: '17pt', fontWeight: 'bold', color: '#9333ea' }}>
                {incomeStatementData.ebitda.length > 1 ?
                  formatPercent(((Math.pow(incomeStatementData.ebitda[incomeStatementData.ebitda.length - 1] / incomeStatementData.ebitda[0], 1 / (incomeStatementData.ebitda.length - 1)) - 1) * 100))
                  : '0%'}
              </div>
              <div style={{ fontSize: '8pt', color: '#6b7280', marginTop: '0.02in' }}>Profitability Growth</div>
            </div>

            <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px', padding: '0.15in' }}>
              <div style={{ fontSize: '8pt', color: '#92400e', marginBottom: '0.04in' }}>Seller's Discretionary Earnings (SDE) CAGR</div>
              <div style={{ fontSize: '17pt', fontWeight: 'bold', color: '#d97706' }}>
                {incomeStatementData.sde.length > 1 ?
                  formatPercent(((Math.pow(incomeStatementData.sde[incomeStatementData.sde.length - 1] / incomeStatementData.sde[0], 1 / (incomeStatementData.sde.length - 1)) - 1) * 100))
                  : '0%'}
              </div>
              <div style={{ fontSize: '8pt', color: '#6b7280', marginTop: '0.02in' }}>Owner Earnings Growth</div>
            </div>
          </div>

          <h2 className="section-title" style={{ fontSize: '14pt', marginBottom: '0.15in' }}>Financial Trends</h2>

          {/* Income Statement Trends */}
          {incomeStatementYears.length > 0 && (
            <div style={{ marginBottom: '0.25in' }}>
              <h3 style={{ fontSize: '14pt', fontWeight: 600, color: '#1e3a8a', marginBottom: '0.1in', textAlign: 'center' }}>Income Statement Trends</h3>
              <svg width="100%" height="250" viewBox="0 0 800 250" xmlns="http://www.w3.org/2000/svg">
                {/* Y-axis labels */}
                {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
                  const maxValue = Math.max(...incomeStatementData.revenue, ...incomeStatementData.ebitda, ...incomeStatementData.netIncome);
                  const value = maxValue * (1 - pct);
                  const y = 50 + (170 * pct);
                  return (
                    <g key={i}>
                      <line x1="60" y1={y} x2="750" y2={y} stroke="#e5e7eb" strokeWidth="1" />
                      <text x="45" y={y + 4} textAnchor="end" fontSize="12" fill="#6b7280">
                        ${(value / 1000000).toFixed(1)}M
                      </text>
                    </g>
                  );
                })}

                {/* X-axis labels */}
                {incomeStatementYears.map((year, i) => {
                  const x = 60 + (i * (690 / (incomeStatementYears.length - 1)));
                  return (
                    <text key={i} x={x} y="235" textAnchor="middle" fontSize="12" fill="#6b7280">
                      {year}
                    </text>
                  );
                })}

                {/* Plot lines */}
                {(() => {
                  const maxValue = Math.max(...incomeStatementData.revenue, ...incomeStatementData.ebitda, ...incomeStatementData.sde, ...incomeStatementData.gross_profit);
                  const getY = (value) => 220 - (value / maxValue * 170);
                  const getX = (index) => 60 + (index * (690 / (incomeStatementYears.length - 1)));

                  return (
                    <>
                      {/* Revenue */}
                      <polyline points={incomeStatementData.revenue.map((val, i) => `${getX(i)},${getY(val)}`).join(' ')} fill="none" stroke="#f97316" strokeWidth="2" />
                      {incomeStatementData.revenue.map((val, i) => (<circle key={`r${i}`} cx={getX(i)} cy={getY(val)} r="4" fill="#f97316" />))}
                      {incomeStatementData.revenue.map((val, i) => (<circle key={`r${i}`} cx={getX(i)} cy={getY(val)} r="2" fill="#ffffff" />))}
                      {incomeStatementData.revenue.map((val, i) => (
                        <text
                          key={i}
                          x={getX(i) + 0}
                          y={getY(val) - 12}
                          textAnchor="middle"
                          fontSize="11"
                          fontWeight="600"
                          fill="#f97316"
                        >
                          {formatShortCurrency(val)}
                        </text>
                      ))}

                      {/* Gross Profit */}
                      <polyline points={incomeStatementData.gross_profit.map((val, i) => `${getX(i)},${getY(val)}`).join(' ')} fill="none" stroke="#14b8a6" strokeWidth="2" />
                      {incomeStatementData.gross_profit.map((val, i) => (<circle key={`e${i}`} cx={getX(i)} cy={getY(val)} r="4" fill="#14b8a6" />))}
                      {incomeStatementData.gross_profit.map((val, i) => (<circle key={`e${i}`} cx={getX(i)} cy={getY(val)} r="2" fill="#ffffff" />))}
                      {incomeStatementData.gross_profit.map((val, i) => (
                        <text
                          key={i}
                          x={getX(i) + 0}
                          y={getY(val) - 12}
                          textAnchor="middle"
                          fontSize="11"
                          fontWeight="600"
                          fill="#14b8a6"
                        >
                          {formatShortCurrency(val)}
                        </text>
                      ))}

                      {/* EBITDA */}
                      <polyline points={incomeStatementData.ebitda.map((val, i) => `${getX(i)},${getY(val)}`).join(' ')} fill="none" stroke="#3b82f6" strokeWidth="2" />
                      {incomeStatementData.ebitda.map((val, i) => (<circle key={`n${i}`} cx={getX(i)} cy={getY(val)} r="4" fill="#3b82f6" />))}
                      {incomeStatementData.ebitda.map((val, i) => (<circle key={`n${i}`} cx={getX(i)} cy={getY(val)} r="2" fill="#ffffff" />))}
                      {incomeStatementData.ebitda.map((val, i) => (
                        <text
                          key={i}
                          x={getX(i) + 10}
                          y={getY(val) + 18}
                          textAnchor="middle"
                          fontSize="11"
                          fontWeight="600"
                          fill="#3b82f6"
                        >
                          {formatShortCurrency(val)}
                        </text>
                      ))}

                      {/* SDE */}
                      <polyline points={incomeStatementData.sde.map((val, i) => `${getX(i)},${getY(val)}`).join(' ')} fill="none" stroke="#8b5cf6" strokeWidth="2" strokeDasharray="4" />
                      {incomeStatementData.sde.map((val, i) => (<circle key={`s${i}`} cx={getX(i)} cy={getY(val)} r="4" fill="#8b5cf6" />))}
                      {incomeStatementData.sde.map((val, i) => (<circle key={`s${i}`} cx={getX(i)} cy={getY(val)} r="2" fill="#ffffff" />))}
                      {incomeStatementData.sde.map((val, i) => (
                        <text
                          key={i}
                          x={getX(i) + 10}
                          y={getY(val) - 8}
                          textAnchor="middle"
                          fontSize="11"
                          fontWeight="600"
                          fill="#8b5cf6"
                        >
                          {formatShortCurrency(val)}
                        </text>
                      ))}
                    </>
                  );
                })()} 

                {/* Legend */}
                <rect x="275" y="5" width="12" height="3" fill="#f97316" />
                <text x="290" y="9" fontSize="11" fill="#6b7280">Revenue</text>
                <rect x="355" y="5" width="12" height="3" fill="#14b8a6" />
                <text x="370" y="9" fontSize="11" fill="#6b7280">Gross Profit</text>
                <rect x="450" y="5" width="12" height="3" fill="#3b82f6" />
                <text x="465" y="9" fontSize="11" fill="#6b7280">EBITDA</text>
                <rect x="520" y="5" width="12" height="3" fill="#8b5cf6" />
                <text x="535" y="9" fontSize="11" fill="#6b7280">SDE</text>
              </svg>
            </div>
          )}

          {/* Balance Sheet Trends */}
          {balanceSheetYears.length > 0 && (
            <div style={{ marginTop: '0.25in' }}>
              <h3 style={{ fontSize: '14pt', fontWeight: 600, color: '#1e3a8a', marginBottom: '0.1in',textAlign: 'center' }}>Balance Sheet Trends</h3>
              <svg width="100%" height="250" viewBox="0 0 800 250" xmlns="http://www.w3.org/2000/svg">
                {/* Y-axis labels */}
                {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
                  const maxValue = Math.max(...balanceSheetData.assets, ...balanceSheetData.liabilities, ...balanceSheetData.equity);
                  const value = maxValue * (1 - pct);
                  const y = 50 + (170 * pct);
                  return (
                    <g key={i}>
                      <line x1="60" y1={y} x2="750" y2={y} stroke="#e5e7eb" strokeWidth="1" />
                      <text x="45" y={y + 4} textAnchor="end" fontSize="12" fill="#6b7280">
                        ${(value / 1000000).toFixed(1)}M
                      </text>
                    </g>
                  );
                })}

                {/* X-axis labels */}
                {balanceSheetYears.map((year, i) => {
                  const x = 60 + (i * (690 / (balanceSheetYears.length - 1)));
                  return (
                    <text key={i} x={x} y="235" textAnchor="middle" fontSize="12" fill="#6b7280">
                      {year}
                    </text>
                  );
                })}

                {/* Plot lines */}
                {(() => {
                  const maxValue = Math.max(...balanceSheetData.assets, ...balanceSheetData.liabilities, ...balanceSheetData.equity);
                  const getY = (value) => 220 - (value / maxValue * 170);
                  const getX = (index) => 60 + (index * (690 / (balanceSheetYears.length - 1)));

                  return (
                    <>
                      {/* Total Assets */}
                      <polyline points={balanceSheetData.assets.map((val, i) => `${getX(i)},${getY(val)}`).join(' ')} fill="none" stroke="#3b82f6" strokeWidth="2.5" />
                      {balanceSheetData.assets.map((val, i) => (<circle key={`a${i}`} cx={getX(i)} cy={getY(val)} r="4" fill="#3b82f6" />))}
                      {balanceSheetData.assets.map((val, i) => (<circle key={`a${i}`} cx={getX(i)} cy={getY(val)} r="2" fill="#ffffff" />))}
                      {balanceSheetData.assets.map((val, i) => (
                        <text
                          key={i}
                          x={getX(i) + 5}
                          y={getY(val) - 12}
                          textAnchor="middle"
                          fontSize="11"
                          fontWeight="600"
                          fill="#3b82f6"
                        >
                          {formatShortCurrency(val)}
                        </text>
                      ))}

                      {/* Total Liabilities */}
                      <polyline points={balanceSheetData.liabilities.map((val, i) => `${getX(i)},${getY(val)}`).join(' ')} fill="none" stroke="#ef4444" strokeWidth="2.5" />
                      {balanceSheetData.liabilities.map((val, i) => (<circle key={`l${i}`} cx={getX(i)} cy={getY(val)} r="4" fill="#ef4444" />))}
                      {balanceSheetData.liabilities.map((val, i) => (<circle key={`l${i}`} cx={getX(i)} cy={getY(val)} r="2" fill="#ffffff" />))}
                      {balanceSheetData.liabilities.map((val, i) => (
                        <text
                          key={i}
                          x={getX(i) - 20}
                          y={getY(val) - 10}
                          textAnchor="middle"
                          fontSize="11"
                          fontWeight="600"
                          fill="#ef4444"
                        >
                          {formatShortCurrency(val)}
                        </text>
                      ))}

                      {/* Total Equity */}
                      <polyline points={balanceSheetData.equity.map((val, i) => `${getX(i)},${getY(val)}`).join(' ')} fill="none" stroke="#22c55e" strokeWidth="2.5" />
                      {balanceSheetData.equity.map((val, i) => (<circle key={`eq${i}`} cx={getX(i)} cy={getY(val)} r="4" fill="#22c55e" />))}
                      {balanceSheetData.equity.map((val, i) => (<circle key={`eq${i}`} cx={getX(i)} cy={getY(val)} r="2" fill="#ffffff" />))}
                      {balanceSheetData.equity.map((val, i) => (
                        <text
                          key={i}
                          x={getX(i) + 20}
                          y={getY(val) + 18}
                          textAnchor="middle"
                          fontSize="11"
                          fontWeight="600"
                          fill="#22c55e"
                        >
                          {formatShortCurrency(val)}
                        </text>
                      ))}
                    </>
                  );
                })()}

                {/* Legend */}
                <rect x="260" y="5" width="12" height="3" fill="#3b82f6" />
                <text x="275" y="9" fontSize="11" fill="#6b7280">Total Assets</text>
                <rect x="360" y="5" width="12" height="3" fill="#ef4444" />
                <text x="375" y="9" fontSize="11" fill="#6b7280">Total Liabilities</text>
                <rect x="480" y="5" width="12" height="3" fill="#22c55e" />
                <text x="495" y="9" fontSize="11" fill="#6b7280">Total Equity</text>
              </svg>
            </div>
          )}
        </div>
      )}

      {/* Page 4/5: Forecast Charts - Advanced Valuations show on separate page */}
      {isAdvanced && (
        <div className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>
          <h1 className="section-title">Growth Forecast Analysis</h1>

        <div style={{ background: '#f9fafb', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0.08in', marginBottom: '0.08in', marginTop: '0.20in' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr', gap: '0.08in' }}>
          <div><h3 style={{ fontSize: '10pt', fontWeight: 600, marginBottom: '0.05in', color: '#374151' }}>Forecast Assumptions:</h3></div>
            <div>
              <p style={{ fontSize: '9pt', color: '#6b7280', margin: 0 }}>Annual Growth Rate: 
              <span style={{ fontSize: '11pt', fontWeight: 'bold', margin: '0.02in 0', color: '#10b981' }}> {forecastParams?.growthRate ? formatPercent(forecastParams.growthRate) : 'N/A'}</span>
              </p>
            </div>
            <div>
              <p style={{ fontSize: '9pt', color: '#6b7280', margin: 0 }}>EBITDA Margin: 
              <span style={{ fontSize: '11pt', fontWeight: 'bold', margin: '0.02in 0', color: '#9333ea' }}> {forecastParams?.ebitdaMargin ? formatPercent(forecastParams.ebitdaMargin) : 
                (business?.revenue && business?.ebitda ? formatPercent( (business.ebitda / business.revenue) * 100) : 'N/A')}
              </span>
              </p>
            </div>
          </div>
        </div>
        
        <div style={{ background: '#f0f9ff', border: '1px solid #3b82f6', borderRadius: '6px', padding: '0.1in', marginBottom: '0.1in' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.05in' }}>
          <div><p style={{ fontSize: '9pt', margin: '0.05in 0' }}>Current Median Value: <strong>{formatShortCurrency(currentValue)}</strong>
          </p></div>
          <div><p style={{ fontSize: '9pt', margin: '0.05in 0' }}>{forecastYearsCount}-Year Projected Value: <strong>{formatShortCurrency(projectedValue)}</strong>
          </p></div>
            <div><p style={{ fontSize: '8pt', margin: '0.05in 0', color: '#6b7280' }}>
              Based on {forecastParams?.growthRate || 15}% annual growth rate with ±{forecastParams?.confidenceRange || 25}% confidence range
            </p>
            </div>
          </div>
        </div>
          

        {/* Business Exit Value Growth Forecast Chart */}
        <div style={{ marginTop: '0.20in' }}>
          <svg width="100%" height="339" viewBox="0 0 968 339" xmlns="http://www.w3.org/2000/svg">
            <text x="484" y="5" textAnchor="middle" fontSize="18" fontWeight="600" fill="#1e3a8a">
              Business Exit Value Growth Forecast
            </text>

            {(() => {
              // Generate forecast with confidence range using actual parameters
              const chartGrowthRate = (forecastParams?.growthRate || 15) / 100; // Convert percentage to decimal
              const chartConfidenceRange = (forecastParams?.confidenceRange || 25) / 100; // Convert percentage to decimal
              const chartYearsCount = forecastParams?.years || 5;


              const years = Array.from({ length: chartYearsCount + 1 }, (_, i) => new Date().getFullYear() + i);

              // Calculate projected values
              const projectedValues = years.map((_, i) => currentValue * Math.pow(1 + chartGrowthRate, i));
              const upperRange = projectedValues.map(val => val * (1 + chartConfidenceRange));
              const lowerRange = projectedValues.map(val => val * (1 - chartConfidenceRange));

              const maxValue = Math.max(...upperRange);
              const minValue = Math.min(...lowerRange);
              const valueRange = maxValue - minValue;

              const getY = (value) => 290 - ((value - minValue) / valueRange * 218);
              const getX = (index) => 121 + (index * (726 / (years.length - 1)));

              // Create points for confidence range polygon
              const upperPoints = upperRange.map((val, i) => `${getX(i)},${getY(val)}`).join(' ');
              const lowerPoints = lowerRange.map((val, i) => `${getX(i)},${getY(val)}`).reverse().join(' ');
              const rangePolygon = upperPoints + ' ' + lowerPoints;

              // Main line points
              const mainPoints = projectedValues.map((val, i) => `${getX(i)},${getY(val)}`).join(' ');

              return (
                <>
                  {/* Y-axis grid lines and labels */}
                  {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
                    const value = minValue + (valueRange * (1 - pct));
                    const y = 73 + (218 * pct);
                    return (
                      <g key={i}>
                        <line x1="121" y1={y} x2="847" y2={y} stroke="#e5e7eb" strokeWidth="1" />
                        <text x="109" y={y + 4} textAnchor="end" fontSize="11" fill="#6b7280">
                          {formatShortCurrency(value)}
                        </text>
                      </g>
                    );
                  })}

                  {/* X-axis labels */}
                  {years.map((year, i) => {
                    const x = getX(i);
                    return (
                      <text key={i} x={x} y="315" textAnchor="middle" fontSize="11" fill="#6b7280">
                        {year}
                      </text>
                    );
                  })}

                  {/* Confidence range (shaded area) */}
                  <polygon
                    points={rangePolygon}
                    fill="#3b82f6"
                    opacity="0.15"
                  />

                  {/* Upper confidence line */}
                  <polyline
                    points={upperPoints}
                    fill="none"
                    stroke="#93c5fd"
                    strokeWidth="1.5"
                    strokeDasharray="5,5"
                  />

                  {/* Lower confidence line */}
                  <polyline
                    points={lowerRange.map((val, i) => `${getX(i)},${getY(val)}`).join(' ')}
                    fill="none"
                    stroke="#93c5fd"
                    strokeWidth="1.5"
                    strokeDasharray="5,5"
                  />

                  {/* Main projection line */}
                  <polyline
                    points={mainPoints}
                    fill="none"
                    stroke="#2563eb"
                    strokeWidth="3"
                  />

                  {/* Data points */}
                  {projectedValues.map((val, i) => (
                    <g key={i}>
                      <circle cx={getX(i)} cy={getY(val)} r="5" fill="#2563eb" />
                      <circle cx={getX(i)} cy={getY(val)} r="3" fill="white" />
                    </g>
                  ))}

                  {/* Value labels on points */}
                  {projectedValues.map((val, i) => (
                    <text
                      key={i}
                      x={getX(i) + 10}
                      y={getY(val) - 12}
                      textAnchor="middle"
                      fontSize="11"
                      fontWeight="600"
                      fill="#1e40af"
                    >
                      {formatShortCurrency(val)}
                    </text>
                  ))}

                  {/* Legend */}
                  <g transform="translate(330, 30)">
                  <line x1="10" y1="0" x2="40" y2="0" stroke="#2563eb" strokeWidth="3" />
                  <text x="45" y="4" fontSize="12" fill="#6b7280">Projected Median Value</text>

                  <line x1="200" y1="0" x2="230" y2="0" stroke="#93c5fd" strokeWidth="1.5" strokeDasharray="5,5" />
                  <text x="235" y="4" fontSize="12" fill="#6b7280">Range</text>
                  </g>
                </>
              );
            })()}
          </svg>
        </div>

        {/* Revenue & EBITDA Growth Chart */}
        <div style={{ marginTop: '0.08in', pageBreakInside: 'avoid' }}>
          <svg width="100%" height="339" viewBox="0 0 968 339" xmlns="http://www.w3.org/2000/svg">
            <text x="484" y="5" textAnchor="middle" fontSize="18" fontWeight="600" fill="#1e3a8a">
              Revenue & EBITDA Growth Forecast
            </text>

            {(() => {
              // Generate EBITDA forecast data
              const currentRevenue = business?.revenue || 0;
              if (currentRevenue === 0) {
                return (
                  <text x="400" y="175" textAnchor="middle" fontSize="14" fill="#6b7280">
                    No revenue data available
                  </text>
                );
              }

              const chartGrowthRate = (forecastParams?.growthRate || 15) / 100;
              const chartYearsCount = forecastParams?.years || 5;


              // Use ebitdaMargin from forecastParams or calculate from current data
              const currentEbitdaMarginPercent = forecastParams?.ebitdaMargin ||
                (business?.ebitda && business?.revenue ? (business.ebitda / business.revenue) * 100 : 20);
              const currentEbitdaMargin = currentEbitdaMarginPercent / 100;


              // Get industry high margin from profit gap
              const industryHighMarginPercent = profitGap?.industry_high_ebitda_margin || 0;
              const industryHighMargin = industryHighMarginPercent / 100;

              // Generate yearly data points
              const years = Array.from({ length: chartYearsCount + 1 }, (_, i) => new Date().getFullYear() + i);
              const forecastData = years.map((_, i) => {
                const projectedRevenue = currentRevenue * Math.pow(1 + chartGrowthRate, i);
                const projectedCurrentEbitda = projectedRevenue * currentEbitdaMargin;
                const projectedTargetEbitda = industryHighMargin > 0 ? projectedRevenue * industryHighMargin : 0;
                const profitGapAmount = Math.max(0, projectedTargetEbitda - projectedCurrentEbitda);

                return {
                  year: years[i],
                  revenue: projectedRevenue,
                  currentEbitda: projectedCurrentEbitda,
                  targetEbitda: projectedTargetEbitda,
                  profitGapAmount: profitGapAmount
                };
              });

              // Calculate scales
              const maxRevenue = Math.max(...forecastData.map(d => d.revenue));
              const maxEbitda = Math.max(...forecastData.map(d => d.targetEbitda || d.currentEbitda));
              const maxValue = Math.max(maxRevenue, maxEbitda);
              const minValue = 0;
              const valueRange = maxValue - minValue;

              const getY = (value) => 290 - ((value - minValue) / valueRange * 218);
              const getX = (index) => 121 + (index * (726 / (years.length - 1)));

              // Create polygon points for stacked areas
              // Revenue area (green fill)
              const revenuePoints = forecastData.map((d, i) => `${getX(i)},${getY(d.revenue)}`);
              const revenueBottomPoints = forecastData.map((_, i) => `${getX(i)},${getY(0)}`).reverse();
              const revenuePolygon = revenuePoints.join(' ') + ' ' + revenueBottomPoints.join(' ');

              // Current EBITDA stacked area (purple)
              const currentEbitdaPoints = forecastData.map((d, i) => `${getX(i)},${getY(d.currentEbitda)}`);
              const currentEbitdaBottomPoints = forecastData.map((_, i) => `${getX(i)},${getY(0)}`).reverse();
              const currentEbitdaPolygon = currentEbitdaPoints.join(' ') + ' ' + currentEbitdaBottomPoints.join(' ');

              // Profit Gap stacked on top of current EBITDA (orange)
              const profitGapTopPoints = forecastData.map((d, i) =>
                `${getX(i)},${getY(d.currentEbitda + d.profitGapAmount)}`
              );
              const profitGapBottomPoints = forecastData.map((d, i) =>
                `${getX(i)},${getY(d.currentEbitda)}`
              ).reverse();
              const profitGapPolygon = profitGapTopPoints.join(' ') + ' ' + profitGapBottomPoints.join(' ');

              // Target EBITDA line points
              const targetEbitdaPoints = forecastData.map((d, i) => `${getX(i)},${getY(d.targetEbitda)}`).join(' ');

              return (
                <>
                  {/* Y-axis grid lines and labels */}
                  {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
                    const value = minValue + (valueRange * (1 - pct));
                    const y = 73 + (218 * pct);
                    return (
                      <g key={i}>
                        <line x1="121" y1={y} x2="847" y2={y} stroke="#e5e7eb" strokeWidth="1" />
                        <text x="100" y={y + 4} textAnchor="end" fontSize="13" fill="#6b7280">
                          {formatShortCurrency(value)}
                        </text>
                      </g>
                    );
                  })}

                  {/* X-axis labels */}
                  {years.map((year, i) => {
                    const x = getX(i);
                    return (
                      <text key={i} x={x} y="315" textAnchor="middle" fontSize="13" fill="#6b7280">
                        {year}
                      </text>
                    );
                  })}

                  {/* Revenue area (green with light opacity) */}
                  <polygon
                    points={revenuePolygon}
                    fill="#22c55e"
                    opacity="0.15"
                  />

                  {/* Revenue line */}
                  <polyline
                    points={revenuePoints.join(' ')}
                    fill="none"
                    stroke="#22c55e"
                    strokeWidth="2"
                  />

                  {/* Current EBITDA stacked area (purple) */}
                  <polygon
                    points={currentEbitdaPolygon}
                    fill="#9333ea"
                    opacity="0.3"
                  />

                  {/* Current EBITDA line */}
                  <polyline
                    points={currentEbitdaPoints.join(' ')}
                    fill="none"
                    stroke="#7c3aed"
                    strokeWidth="2"
                  />

                  {/* Profit Gap stacked area (orange) */}
                  {industryHighMargin > 0 && (
                    <polygon
                      points={profitGapPolygon}
                      fill="#f59e0b"
                      opacity="0.3"
                    />
                  )}

                  {/* Target EBITDA dashed line (purple) */}
                  {industryHighMargin > 0 && (
                    <polyline
                      points={targetEbitdaPoints}
                      fill="none"
                      stroke="#ee4242"
                      strokeWidth="2"
                      strokeDasharray="5,5"
                    />
                  )}

                  {/* Data points and value labels for revenue */}
                  {forecastData.map((d, i) => (
                    <g key={`rev-${i}`}>
                      <circle cx={getX(i)} cy={getY(d.revenue)} r="5" fill="#22c55e" />
                      <circle cx={getX(i)} cy={getY(d.revenue)} r="3" fill="white" />
                      <text
                        x={getX(i)}
                        y={getY(d.revenue) - 12}
                        textAnchor="middle"
                        fontSize="11"
                        fontWeight="600"
                        fill="#15803d"
                      >
                        {formatShortCurrency(d.revenue)}
                      </text>
                    </g>
                  ))}

                  {/* Data points and value labels for current EBITDA */}
                  {forecastData.map((d, i) => (
                    <g key={`curr-ebitda-${i}`}>
                      <circle cx={getX(i)} cy={getY(d.currentEbitda)} r="5" fill="#7c3aed" />
                      <circle cx={getX(i)} cy={getY(d.currentEbitda)} r="3" fill="white" />
                      <text
                        x={getX(i) + 25}
                        y={getY(d.currentEbitda) + 20}
                        textAnchor="middle"
                        fontSize="11"
                        fontWeight="600"
                        fill="#6b21a8"
                        outline="1px solid #ffffff"
                      >
                        {formatShortCurrency(d.currentEbitda)}
                      </text>
                    </g>
                  ))}

                  {/* Data points and value labels for target EBITDA */}
                  {industryHighMargin > 0 && forecastData.map((d, i) => (
                    <g key={`target-ebitda-${i}`}>
                      <circle cx={getX(i)} cy={getY(d.targetEbitda)} r="5" fill="#ee4242" />
                      <circle cx={getX(i)} cy={getY(d.targetEbitda)} r="3" fill="white" />
                      <text
                        x={getX(i)}
                        y={getY(d.targetEbitda) - 12}
                        textAnchor="middle"
                        fontSize="11"
                        fontWeight="600"
                        fill="#ee4242"
                      >
                        {formatShortCurrency(d.targetEbitda)}
                      </text>
                    </g>
                  ))}

                  {/* Legend */}
                  <g transform="translate(200, 40)">
                    {/* Revenue */}
                    <line x1="0" y1="0" x2="25" y2="0" stroke="#22c55e" strokeWidth="2" />
                    <text x="30" y="4" fontSize="11" fill="#6b7280">Revenue</text>

                    {/* Target EBITDA */}
                    {industryHighMargin > 0 && (
                      <>
                        <line x1="390" y1="0" x2="415" y2="0" stroke="#ee4242" strokeWidth="2" strokeDasharray="5,5" />
                        <text x="420" y="4" fontSize="11" fill="#6b7280">Target EBITDA</text>
                      </>
                    )}

                    {/* Current EBITDA */}
                    <rect x="120" y="-6" width="20" height="12" fill="#9333ea" opacity="0.5" />
                    <text x="145" y="4" fontSize="11" fill="#6b7280">Current EBITDA</text>

                    {/* Profit Gap */}
                    {industryHighMargin > 0 && (
                      <>
                        <rect x="270" y="-6" width="20" height="12" fill="#f59e0b" opacity="0.5" />
                        <text x="295" y="4" fontSize="11" fill="#6b7280">Profit Gap</text>
                      </>
                    )}
                  </g>
                </>
              );
            })()}
          </svg>
        </div>
      </div>
      )}

      {/* Page 4 (Simple) / Page 5 (Advanced): Next Steps */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Recommended Next Steps</h1>

        <div className="next-steps">
          <div className="step">
            <h3>1. Validate Financial Data</h3>
            <p>
              Review and ensure all financial inputs accurately reflect your business performance.
              Update revenue, EBITDA, and other key metrics regularly for an accurate range of values.
            </p>
          </div>

          <div className="step">
            <h3>2. Complete Gap Assessments</h3>
            <ul>
              <li>Complete the Business Gap Assessment to understand business attractiveness and readiness metrics</li>
              <li>Complete the Personal Gap Assessment to understand personal financial goals and readiness metrics</li>
              <li>Assess the Wealth Gap to determine if exit proceeds will meet financial goals</li>
              <li>Assess the Profit Gap to identify margin improvement opportunities</li>
              <li>Assess the Value Gap to understand value enhancement opportunities</li>
            </ul>
          </div>

          <div className="step">
            <h3>3. Focus on Value Enhancement</h3>
            <ul>
              <li>Improve EBITDA margins through operational efficiency</li>
              <li>Reduce customer concentration risk</li>
              <li>Document systems and processes to minimize owner dependency</li>
              <li>Build a strong management team</li>
              <li>Protect intellectual property and competitive advantages</li>
            </ul>
          </div>

          <div className="step">
            <h3>4. Engage Professional Advisors</h3>
            <ul>
              <li>Consult with a Certified Exit Planning Advisor (CEPA)</li>
              <li>Consider a formal valuation from a credentialed valuator (CVA, ASA, ABV)</li>
              <li>Work with M&A advisors when ready to pursue a transaction</li>
              <li>Engage tax, investment, insurance, financial, and legal counsel for exit planning strategies</li>
            </ul>
          </div>

          <div className="step">
            <h3>5. Monitor Progress</h3>
            <p>
              Re-run range of value estimates and gap assessments quarterly to track progress.
              Business value ranges are dynamic and change with performance, market conditions, and strategic initiatives.
              Regular monitoring helps you stay on track toward your exit goals.
            </p>
          </div>
        </div>

        <div className="disclosure-box" style={{ marginTop: '0.3in' }}>
          <h3>Important Disclosure</h3>
          <p>
            <strong>PROFESSIONAL DISCLOSURE:</strong> This business opinion of value report has been prepared using automated
            valuation methodologies based on industry-standard practices. This report is intended for informational
            and planning purposes only and should not be relied upon as the sole basis for any transaction.
          </p>
          <p>
            <strong>NOT A FORMAL APPRAISAL:</strong> This report does not constitute a formal business appraisal.
            For regulatory, litigation, or tax purposes, a formal appraisal by a credentialed valuator is required.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrintValuationDashboard;
